import type { GenerateReportRequest, GenerateReportResponse, ParentCustomerRequest, RawCostReportRequest, RawCostUsageResponse, reportTypeObj, ServiceFilterRequest, ServiceFilterResponse, StampCustomerMappingResponse, StampCustomerRequest } from "../types/cost-report.types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../../../store/store";
import { costService } from "../services/costService";
import {
  buildParentCustomersPayloadKey,
  buildRawCostUsagePayloadKey,
  buildServiceFiltersByCategoryPayloadKey,
} from "../utils/shared/costRequestKey.utils";
import { getErrorMessage, isAbortError } from "../utils/shared/error.utils";
import type { CostReportState } from "../types/cost-report.types";

/**
 * State for reports, export, service filters, recipients and report generation
 */

const initialState: CostReportState = {
  exportLoading: false,
  exportError: null,

  parentCustomers: [],
  parentCustomersLoading: false,
  parentCustomersError: null,

  generateReportLoading: false,
  generateReportError: null,

  recipientList: [],
  recipientsStatus: "idle",
  currentReportRequestId: null,

  reportTypes: null,
  reportTypesStatus: "idle",
  rawCostUsageData: null,
  rawCostUsageLoading: false,
  rawCostUsageError: null,

  serviceFiltersByCategory: null,
  serviceFiltersByCategoryLoading: false,
  serviceFiltersByCategoryError: null,

  stampCustomerMappings: null,
  stampCustomerMappingsLoading: false,
  stampCustomerMappingsError: null,
};

// Abort controller
let costReportAbortController: AbortController | null = null;
let lastParentCustomersPayloadKey: string | null = null;
// const lastServiceFiltersPayloadKey: string | null = null;
let lastRawCostUsagePayloadKey: string | null = null;
let lastServiceFiltersByCategoryPayloadKey: string | null = null;
let activeRawCostUsageRequestId: string | null = null;

/***
 * Fetches raw cost usage data by category with request cancellation and duplicate prevention.
 * Aborts previous request if still pending. Skips if payload is unchanged.
 * @param err - Error object
 * @param fallback - Default message if extraction fails
 * @returns Error message string
 */
export const fetchRawCostUsageByCategory = createAsyncThunk<
  RawCostUsageResponse,
  RawCostReportRequest,
  { state: RootState; rejectValue: string }
>(
  "costReport/fetchRawCostUsageByCategory",
  async (payload, { rejectWithValue }) => {
    if (costReportAbortController) {
      costReportAbortController.abort();
    }

    costReportAbortController = new AbortController();

    try {
      return await costService.GetRawCostUsageByCategory(payload, {
        signal: costReportAbortController.signal,
      });
    } catch (err: unknown) {
      if (isAbortError(err)) {
        return rejectWithValue("aborted");
      }

      return rejectWithValue(getErrorMessage(err, "Something went wrong"));
    }
  },
  {
    condition: (payload, { getState }) => {
      const state = getState() as RootState;
      const key = buildRawCostUsagePayloadKey(payload);

      // Skip if already loading with same payload (prevent duplicate in-flight requests)
      if (
        state.costReport.rawCostUsageLoading &&
        key === lastRawCostUsagePayloadKey
      ) {
        return false;
      }

      // Always fetch if no data (page refresh, Redux reset)
      if (state.costReport.rawCostUsageData === null) {
        // Set key immediately to prevent race condition with subsequent calls
        lastRawCostUsagePayloadKey = key;
        return true;
      }

      // Skip if payload unchanged
      if (key === lastRawCostUsagePayloadKey) {
        return false;
      }

      // Set key and proceed with fetch
      lastRawCostUsagePayloadKey = key;
      return true;
    },
  },
);
/**
 * Fetches service filters by category with duplicate prevention.
 * Skips if payload is unchanged to avoid redundant API calls.
 * @param err - Error object
 * @param fallback - Default message if extraction fails
 * @returns Error message string
 */

export const fetchServiceFiltersByCategory = createAsyncThunk<
  ServiceFilterResponse,
  ServiceFilterRequest,
  { state: RootState; rejectValue: string }
>(
  "costReport/fetchServiceFiltersByCategory",
  async (payload, { rejectWithValue }) => {
    try {
      return await costService.GetServiceFiltersByCategory(payload);
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to load service filters"),
      );
    }
  },
  {
    condition: (payload, { getState }) => {
      const state = getState() as RootState;
      const key = buildServiceFiltersByCategoryPayloadKey(payload);

      // Skip if already loading with same payload
      if (
        state.costReport.serviceFiltersByCategoryLoading &&
        key === lastServiceFiltersByCategoryPayloadKey
      ) {
        return false;
      }

      // Always fetch if no data (page refresh, Redux reset)
      if (state.costReport.serviceFiltersByCategory === null) {
        // Set key immediately to prevent race condition with subsequent calls
        lastServiceFiltersByCategoryPayloadKey = key;
        return true;
      }

      // Skip if payload unchanged
      if (key === lastServiceFiltersByCategoryPayloadKey) {
        return false;
      }

      // Set key and proceed with fetch
      lastServiceFiltersByCategoryPayloadKey = key;
      return true;
    },
  },
);

/**
 *  Fetches stamp customer mappings for given subscriptions.
 * Skips if payload is unchanged to avoid redundant API calls.
 * @param err - Error object
 * @param fallback - Default message if extraction fails
 * @returns Error message string
 */
export const fetchStampCustomerMappings = createAsyncThunk<
  StampCustomerMappingResponse,
  StampCustomerRequest,
  { state: RootState; rejectValue: string }
>(
  "costReport/fetchStampCustomerMappings",
  async (payload, { rejectWithValue }) => {
    try {
      return await costService.GetStampCustomerMappings(payload);
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to load stamp customer mappings"),
      );
    }
  },
);

/* ===================== GENERATE REPORT ===================== */

/**
 * Queues cost report generation as a background job.
 * Returns correlation ID for SignalR job tracking.
 * @param payload - Report generation request with email options
 * @returns Response with correlation ID for job tracking
 */
export const generateCostReportByCustomer = createAsyncThunk<
  GenerateReportResponse,
  GenerateReportRequest,
  { rejectValue: string }
>(
  "costReport/generateReportByCustomer",
  async (payload, { rejectWithValue }) => {
    try {
      return await costService.GenerateCostReportByCustomer(payload);
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err, "Failed to generate report"));
    }
  },
);

/* ===================== REPORT TYPES ===================== */

/**
 * Fetches available report types for cost reporting.
 * Skips if already loaded or loading.
 * @returns Array of report type objects
 */
export const fetchCostReportTypes = createAsyncThunk<
  reportTypeObj[],
  void,
  { rejectValue: string }
>(
  "costReport/fetchCostReportTypes",
  async (_, { rejectWithValue }) => {
    try {
      return await costService.GetCostReportTypes();
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to load ReportTypes"),
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const status = state.costReport.reportTypesStatus;
      return status !== "loading" && status !== "succeeded";
    },
  },
);

/* ===================== PARENT CUSTOMERS ===================== */

/**
 * Fetches parent customers available for selected subscriptions.
 * Skips duplicate requests based on payload comparison.
 * @param payload - Request with subscription IDs
 * @returns Array of parent customer names
 */
export const fetchParentCustomersBySubscriptions = createAsyncThunk<
  string[],
  ParentCustomerRequest,
  { state: RootState; rejectValue: string }
>(
  "costReport/fetchParentCustomersBySubscriptions",
  async (payload, { rejectWithValue }) => {
    try {
      return await costService.GetParentCustomersBySubscriptions(payload);
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err, "Failed to load customers"));
    }
  },
  {
    condition: (payload) => {
      const key = buildParentCustomersPayloadKey(payload);
      return key !== lastParentCustomersPayloadKey;
    },
  },
);

/* ===================== RECIPIENTS ===================== */

/**
 * Fetches available email recipients for cost report delivery.
 * Skips if already loaded or loading.
 * @returns Array of email addresses
 */
export const fetchCostRecipients = createAsyncThunk<
  string[],
  void,
  { rejectValue: string }
>(
  "costReport/fetchCostRecipients",
  async (_, { rejectWithValue }) => {
    try {
      return await costService.GetCostRecipients();
    } catch (err: unknown) {
      return rejectWithValue(getErrorMessage(err, "Failed to load Recipients"));
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const status = state.costReport.recipientsStatus;
      return status !== "loading" && status !== "succeeded";
    },
  },
);

/* ===================== SLICE ===================== */
const costReportSlice = createSlice({
  name: "costReport",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    /* GENERATE REPORT */
    builder
      .addCase(generateCostReportByCustomer.pending, (state) => {
        state.generateReportLoading = true;
        state.generateReportError = null;
      })
      .addCase(generateCostReportByCustomer.fulfilled, (state) => {
        state.generateReportLoading = false;
      })
      .addCase(generateCostReportByCustomer.rejected, (state, action) => {
        state.generateReportLoading = false;
        state.generateReportError =
          action.payload || "Failed to generate report";
      });

    /* PARENT CUSTOMERS */
    builder
      .addCase(fetchParentCustomersBySubscriptions.pending, (state) => {
        state.parentCustomersLoading = true;
        state.parentCustomersError = null;
      })
      .addCase(
        fetchParentCustomersBySubscriptions.fulfilled,
        (state, action) => {
          state.parentCustomersLoading = false;
          state.parentCustomers = action.payload;
          lastParentCustomersPayloadKey = buildParentCustomersPayloadKey(
            action.meta.arg,
          );
        },
      )
      .addCase(
        fetchParentCustomersBySubscriptions.rejected,
        (state, action) => {
          state.parentCustomersLoading = false;
          state.parentCustomersError =
            action.payload || "Failed to load customers";
        },
      );

    /* RECIPIENTS */

    builder
      .addCase(fetchCostRecipients.pending, (state) => {
        state.recipientsStatus = "loading";
      })
      .addCase(fetchCostRecipients.fulfilled, (state, action) => {
        state.recipientList = action.payload;
        state.recipientsStatus = "succeeded";
      })
      .addCase(fetchCostRecipients.rejected, (state) => {
        state.recipientsStatus = "failed";
      });
    /* FETCH REPORT TYPES */
    builder
      .addCase(fetchCostReportTypes.pending, (state) => {
        state.reportTypesStatus = "loading";
      })
      .addCase(fetchCostReportTypes.fulfilled, (state, action) => {
        state.reportTypes = action.payload;
        state.reportTypesStatus = "succeeded";
      })
      .addCase(fetchCostReportTypes.rejected, (state) => {
        state.reportTypesStatus = "failed";
      })
      /* FETCH RAW COST USAGE BY CATEGORY */
      .addCase(fetchRawCostUsageByCategory.pending, (state, action) => {
        activeRawCostUsageRequestId = action.meta.requestId;
        state.rawCostUsageLoading = true;
        state.rawCostUsageError = null;
      })
      .addCase(fetchRawCostUsageByCategory.fulfilled, (state, action) => {
        if (action.meta.requestId !== activeRawCostUsageRequestId) {
          return;
        }

        activeRawCostUsageRequestId = null;
        state.rawCostUsageLoading = false;
        state.rawCostUsageData = action.payload;

        lastRawCostUsagePayloadKey = buildRawCostUsagePayloadKey(
          action.meta.arg,
        );
      })
      .addCase(fetchRawCostUsageByCategory.rejected, (state, action) => {
        if (action.meta.requestId !== activeRawCostUsageRequestId) {
          return;
        }

        // Keep loading owned by the latest in-flight request only.
        activeRawCostUsageRequestId = null;
        state.rawCostUsageLoading = false;

        if (action.payload === "aborted") {
          return;
        }

        state.rawCostUsageError =
          (action.payload as string) ??
          action.error.message ??
          "Failed to load raw cost usage";
      })
      /* FETCH SERVICE FILTERS BY CATEGORY */
      .addCase(fetchServiceFiltersByCategory.pending, (state) => {
        state.serviceFiltersByCategoryLoading = true;
        state.serviceFiltersByCategoryError = null;
      })
      .addCase(fetchServiceFiltersByCategory.fulfilled, (state, action) => {
        state.serviceFiltersByCategoryLoading = false;
        state.serviceFiltersByCategory = action.payload;

        lastServiceFiltersByCategoryPayloadKey =
          buildServiceFiltersByCategoryPayloadKey(action.meta.arg);
      })
      .addCase(fetchServiceFiltersByCategory.rejected, (state, action) => {
        state.serviceFiltersByCategoryLoading = false;

        state.serviceFiltersByCategoryError =
          (action.payload as string) ??
          action.error.message ??
          "Failed to load service filters";
      })
      /* FETCH STAMP CUSTOMER MAPPINGS */
      .addCase(fetchStampCustomerMappings.pending, (state) => {
        state.stampCustomerMappingsLoading = true;
        state.stampCustomerMappingsError = null;
      })
      .addCase(fetchStampCustomerMappings.fulfilled, (state, action) => {
        state.stampCustomerMappingsLoading = false;
        state.stampCustomerMappings = action.payload;
      })
      .addCase(fetchStampCustomerMappings.rejected, (state, action) => {
        state.stampCustomerMappingsLoading = false;

        state.stampCustomerMappingsError =
          (action.payload as string) ??
          action.error.message ??
          "Failed to load stamp customer mappings";
      });
  },
});

export default costReportSlice.reducer;
